<template>
    <v-container fluid>
        <!-- Header -->
        <v-row>
            <v-col
                style="display: flex; align-content: center; align-items: center;"
                cols="6"
                lg="8"
                md="8"
                sm="6"
            >
                <h3>Equipment Warranty</h3>
            </v-col>
            <v-col
                style="text-align: right;"
                cols="6"
                lg="4"
                md="4"
                sm="6"
            >
                <v-tooltip left>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            v-bind="attrs"
                            v-on="on"
                            color="var(--color__main)"
                            dark
                            class="mr-2"
                            :loading="loadingPdf"
                            @click="exportPdf"
                        >
                            <v-icon left>mdi-file-pdf-box</v-icon>
                            Export PDF
                        </v-btn>
                    </template>
                    <span>Export PDF</span>
                </v-tooltip>
                <v-tooltip left>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            v-bind="attrs"
                            v-on="on"
                            :to="{ name: 'equipmentWarrantyForm', params: { id: 0 } }"
                        >
                            <v-icon left>mdi-plus</v-icon>
                            Add New
                        </v-btn>
                    </template>
                    <span>Add New</span>
                </v-tooltip>
            </v-col>
        </v-row>

        <!-- Summary Cards -->
        <v-row class="mt-2 mb-2">
            <v-col cols="6" lg="3" md="3" sm="6">
                <v-card outlined>
                    <v-card-text class="text-center">
                        <div class="text-h4 font-weight-bold" style="color: #14435B;">{{ summary.total }}</div>
                        <div class="caption">Total Products</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" lg="3" md="3" sm="6">
                <v-card outlined>
                    <v-card-text class="text-center">
                        <div class="text-h4 font-weight-bold" style="color: #4CAF50;">{{ summary.inWarranty }}</div>
                        <div class="caption">In Warranty</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" lg="3" md="3" sm="6">
                <v-card outlined>
                    <v-card-text class="text-center">
                        <div class="text-h4 font-weight-bold" style="color: #FF9800;">{{ summary.nearExpiry }}</div>
                        <div class="caption">Near Expiry</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" lg="3" md="3" sm="6">
                <v-card outlined>
                    <v-card-text class="text-center">
                        <div class="text-h4 font-weight-bold" style="color: #F44336;">{{ summary.expired }}</div>
                        <div class="caption">Expired</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Filters -->
        <v-row>
            <v-col cols="12">
                <v-form @submit.prevent="getRegisters">
                    <v-text-field
                        v-model="filter.fastSearch"
                        label="Quick Search"
                        prepend-inner-icon="mdi mdi-magnify"
                        single-line
                        outlined
                        @click:prepend-inner="getRegisters"
                        dense
                    ></v-text-field>
                </v-form>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" lg="4" md="4" sm="12">
                <label>Customer</label>
                <v-combobox
                    v-model="filter.customerSelected"
                    :items="listCustomer"
                    item-text="fullName"
                    item-value="id"
                    clearable
                    single-line
                    outlined
                    dense
                    @change="applyFilters"
                ></v-combobox>
            </v-col>
            <v-col cols="12" lg="4" md="4" sm="12">
                <label>Product</label>
                <v-combobox
                    v-model="filter.productSelected"
                    :items="listProduct"
                    item-text="description"
                    item-value="id"
                    clearable
                    single-line
                    outlined
                    dense
                    @change="applyFilters"
                ></v-combobox>
            </v-col>
            <v-col cols="12" lg="4" md="4" sm="12">
                <label>Status</label>
                <v-select
                    v-model="filter.statusSelected"
                    :items="statusOptions"
                    item-text="label"
                    item-value="value"
                    clearable
                    single-line
                    outlined
                    dense
                    @change="applyFilters"
                ></v-select>
            </v-col>
        </v-row>

        <!-- Table -->
        <v-row>
            <v-col cols="12">
                <v-data-table
                    :headers="headers"
                    :items="filteredList"
                    :loading="loading"
                    :items-per-page="15"
                    no-data-text="No records found"
                >
                    <template v-slot:item.jobId="{ item }">
                        <v-tooltip left>
                            <template v-slot:activator="{ on }">
                                <a @click="goToServiceDetails(item.idService)" v-on="on" target="_blank">
                                    {{ item.jobId == 'Job #' ? '' : item.jobId }}
                                </a>
                            </template>
                            <span>Job Details</span>
                        </v-tooltip>
                    </template>

                    <template v-slot:item.warrantyStatus="{ item }">
                        <v-chip
                            small
                            :color="getStatusColor(item.warrantyStatus)"
                            dark
                        >
                            {{ item.warrantyStatus }}
                        </v-chip>
                    </template>
                    <template v-slot:item.action="{ item }">
                        <ActionList
                            :id="item.id"
                            :link="'equipmentWarrantyForm'"
                            :showButtons="{ edit: true, delete: true }"
                            @confirmDelete="confirmDelete"
                        />
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <!-- Confirmation Dialog -->
        <ActionDialog
            v-on:methodConfirmToCall="dialog.methodConfirm"
            :showDialog.sync="dialog.show"
            :headerTitle="dialog.headerText"
            :bodyText="dialog.bodyText"
            :params="dialog.params"
        />
    </v-container>
</template>

<script>
    import Helpers from '@/utilities/Helpers';
    import ActionList from "@/components/Layout/ActionList";
    import ActionDialog from "@/components/Layout/ActionDialog";

    export default ({

        mixins: [Helpers],

        components: {
            ActionList,
            ActionDialog,
        },

        computed: {
            filteredList() {
                let list = [...this.listWarranty];

                if (this.filter.fastSearch) {
                    const q = this.filter.fastSearch.toLowerCase();
                    list = list.filter(w =>
                        (w.customerName       || '').toLowerCase().includes(q) ||
                        (w.productDescription || '').toLowerCase().includes(q) ||
                        (w.serialNumber       || '').toLowerCase().includes(q) ||
                        (w.jobId              || '').toLowerCase().includes(q) ||
                        (w.warrantyStatus     || '').toLowerCase().includes(q) ||
                        (w.installDateFormatted     || '').toLowerCase().includes(q) ||
                        (w.expirationDateFormatted  || '').toLowerCase().includes(q) ||
                        w.id.toString().includes(q)
                    );
                }

                if (this.filter.customerSelected) {
                    list = list.filter(w => w.idCustomer === this.filter.customerSelected.id);
                }

                if (this.filter.productSelected) {
                    list = list.filter(w => w.idProduct === this.filter.productSelected.id);
                }

                if (this.filter.statusSelected) {
                    list = list.filter(w => w.warrantyStatus === this.filter.statusSelected);
                }

                return list;
            },

            summary() {
                return {
                    total:      this.filteredList.length,
                    inWarranty: this.filteredList.filter(w => w.warrantyStatus === 'In Warranty').length,
                    nearExpiry: this.filteredList.filter(w => w.warrantyStatus === 'Near Expiry').length,
                    expired:    this.filteredList.filter(w => w.warrantyStatus === 'Expired').length,
                };
            },
        },
        data: () => ({
            loading: false,
            loadingPdf: false,

            listWarranty: [],
            listCustomer: [],
            listProduct: [],

            filter: {
                fastSearch: '',
                customerSelected: null,
                productSelected: null,
                statusSelected: null,
            },

            statusOptions: [
                { label: 'In Warranty', value: 'In Warranty' },
                { label: 'Near Expiry', value: 'Near Expiry' },
                { label: 'Expired', value: 'Expired' },
            ],

            headers: [
                { text: 'ID',             value: 'id',                sortable: true,  width: '60px' },
                { text: 'Job ID',         value: 'jobId',             sortable: true  },
                { text: 'Customer',       value: 'customerName',      sortable: true  },
                { text: 'Product',        value: 'productDescription',sortable: true  },
                { text: 'Serial Number',  value: 'serialNumber',      sortable: true  },
                { text: 'Install Date',   value: 'installDateFormatted', sortable: true },
                { text: 'Expiration Date',value: 'expirationDateFormatted', sortable: true },
                { text: 'Status',         value: 'warrantyStatus',    sortable: true  },
                { text: 'Actions',        value: 'action',            sortable: false, align: 'center', width: '100px' },
            ],

            dialog: {
                show: false,
                headerText: '',
                bodyText: '',
                methodConfirm: (() => {}),
                params: null
            },
        }),

        methods: {
            goToServiceDetails(id) {
                if (id != null) {
                    this.$router.push({ name: "serviceDetails", params: {id: id} });
                }
            },

            getStatusColor(status) {
                if (status === 'Expired') return '#F44336';
                if (status === 'Near Expiry') return '#FF9800';
                return '#4CAF50';
            },

            applyFilters() {},

            updateSummary() {},

            async getRegisters() {
                this.loading = true;
                this.listWarranty = await this.$store.dispatch("equipmentWarrantyModule/List");
                this.loading = false;
            },

            async getLists() {
                this.listCustomer = await this.$store.dispatch("customerModule/List");
                this.listProduct = await this.$store.dispatch("productModule/List");
            },

            confirmDelete(id) {
                this.dialog = {
                    show: true,
                    headerText: 'Confirmation',
                    bodyText: 'You will DELETE this warranty record, confirm your decision?',
                    methodConfirm: this.deleteRecord,
                    params: id
                };
            },

            async deleteRecord(id) {
                this.showLoading();
                const result = await this.$store.dispatch("equipmentWarrantyModule/Delete", id);
                if (result && result.success === false) {
                    this.showToast("error", "Error!", result.message);
                } else {
                    this.showToast("success", "Success!", "Record deleted successfully.");
                    await this.getRegisters();
                }
                this.hideLoading();
            },

            exportPdf() {
                this.loadingPdf = true;

                const idCustomer = this.filter.customerSelected ? this.filter.customerSelected.id : 0;
                const idProduct  = this.filter.productSelected  ? this.filter.productSelected.id  : 0;

                this.$vanKirkApi.get(`equipmentwarranty/export-pdf`, {
                    responseType: "blob",
                    params: { idCustomer, idProduct }
                })
                .then((response) => {
                    const blob = new Blob([response.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    window.open(url);
                    this.loadingPdf = false;
                })
                .catch(error => {
                    let errorMessage = error.response && error.response.status === 400
                        ? `Error to generate PDF: [${error.response.data}]`
                        : `Error to generate PDF: [${error}]`;
                    this.showToast("error", "Error!", errorMessage);
                    this.loadingPdf = false;
                });
            },
        },

        async created() {
            this.showLoading();
            await this.getLists();
            await this.getRegisters();
            this.hideLoading();
        }
    })
</script>
